import { FC, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useIntl } from 'react-intl';
import CloseIcon from '@mui/icons-material/Close';

type BaseDialogProps = {
  open: boolean;
  scroll?: DialogProps['scroll'];
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  title?: React.ReactNode;
  children: React.ReactNode;
  showActions?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};

export const BaseDialog: FC<BaseDialogProps> = ({
  open,
  scroll,
  maxWidth = 'sm',
  fullWidth = true,
  title,
  children,
  showActions = true,
  onClose,
  onConfirm,
}) => {
  const intl = useIntl();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const handleConfirm = async () => {
    if (!onConfirm || confirmLoading) return;
    try {
      setConfirmLoading(true);
      await onConfirm();
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} scroll={scroll} maxWidth={maxWidth} fullWidth={fullWidth}>
      <div style={{ height: 20 }}>
        <IconButton
          disabled={confirmLoading}
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: scroll ? 20 : 10,
            top: 10,
            zIndex: 9999,
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      {title && (
        <DialogTitle>
          <div
            style={{
              flex: 1,
              textAlign: 'center',
            }}
          >
            {title}
          </div>
        </DialogTitle>
      )}

      {children && <DialogContent>{children}</DialogContent>}
      {showActions && (
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 3,
          }}
        >
          <Button variant="contained" onClick={onClose} disabled={confirmLoading}>
            {intl.formatMessage({ id: 'recruit.button.cancel' })}
          </Button>

          <Button variant="contained" color="secondary" onClick={handleConfirm} disabled={confirmLoading}>
            {intl.formatMessage({ id: 'recruit.button.confirm' })}
            {confirmLoading && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
